import { sendDataToDataLayer } from 'app/util/analytics/analytics';
import { useGACheckoutDTOFactory } from '../factories/ga-checkout-dto.factory';
import { useCheckoutFrameStore } from 'app/context/checkoutStore';
import { apiAmountToCurrencyString } from 'app/util/currency';
import { PaymentMethodType } from 'app/util/constants';
import { AnalyticsEvents } from 'app/util/analytics/constants';
import { useSearchParams } from 'react-router-dom';
import { useIsEmbedded } from 'app/pages/frames/hooks/useIsEmbedded';

export const useAnalytics = () => {
  const { checkoutRequest, entity } = useCheckoutFrameStore();
  const { parseGAPurchaseDTO } = useGACheckoutDTOFactory();
  const { isEmbedded } = useIsEmbedded();
  const selectedPaymentMethod = PaymentMethodType.Card;
  const [searchParams] = useSearchParams();
  const emitDatalayer = !!searchParams.get('emit_gtm') || false;

  const sendFinish3DSValidationDatalayer = (
    validationResult: boolean,
    requireChallenge: boolean,
    threeDSErrorCode?: number,
  ) =>
    sendDataToDataLayer(
      AnalyticsEvents.finishThreeDSValidation,
      {
        ...buildDatalayer(),
        threeDS_result: validationResult ? 'success' : 'failure',
        require_challenge: requireChallenge,
        ...(threeDSErrorCode ? { three_ds_error_code: threeDSErrorCode } : {}),
      },
      emitDatalayer,
    );

  const sendStart3DSDatalayer = (requireChallenge: boolean) =>
    sendDataToDataLayer(
      AnalyticsEvents.startThreeDSValidation,
      {
        ...buildDatalayer(),
        require_challenge: requireChallenge,
      },
      emitDatalayer,
    );

  const sendShow3DSChallengeDatalayer = () =>
    sendDataToDataLayer(AnalyticsEvents.showThreeDSChallenge, { ...buildDatalayer() }, emitDatalayer);

  const sendHide3DSChallengeDatalayer = () =>
    sendDataToDataLayer(AnalyticsEvents.hideThreeDSChallenge, { ...buildDatalayer() }, emitDatalayer);

  const send3DSError = (description: string, error: string, fatal = false) =>
    sendDataToDataLayer(AnalyticsEvents.exception, { ...buildDatalayer(), description, error, fatal }, emitDatalayer);

  const buildDatalayer = () => {
    const formattedAmount = Number(apiAmountToCurrencyString(checkoutRequest?.amount ?? 0));
    const gaCheckoutDTO = {
      ...parseGAPurchaseDTO(checkoutRequest!, entity!, formattedAmount, selectedPaymentMethod!),
      is_embedded_three_ds: isEmbedded,
    };
    return gaCheckoutDTO;
  };

  return {
    send3DSError,
    sendShow3DSChallengeDatalayer,
    sendHide3DSChallengeDatalayer,
    sendFinish3DSValidationDatalayer,
    sendStart3DSDatalayer,
    emitDatalayer,
  };
};
